'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useSyncExternalStore } from 'react';
interface Clock {
  now: number;
  listeners: Set<() => void>;
  intervalId?: ReturnType<typeof setInterval>;
}

/** One clock per interval, shared by every subscriber and dropped when the last one leaves. */
const clocks = new Map<number, Clock>();
function getClock(intervalMs: number): Clock {
  let clock = clocks.get(intervalMs);
  if (!clock) {
    clock = {
      now: Date.now(),
      listeners: new Set()
    };
    clocks.set(intervalMs, clock);
  }
  return clock;
}
function subscribe(intervalMs: number, listener: () => void): () => void {
  const clock = getClock(intervalMs);
  clock.listeners.add(listener);
  if (clock.intervalId === undefined) {
    // React re-reads the snapshot after subscribing, so a stale idle clock re-renders once.
    clock.now = Date.now();
    clock.intervalId = setInterval(() => {
      clock.now = Date.now();
      for (const notify of clock.listeners) notify();
    }, intervalMs);
  }
  return () => {
    clock.listeners.delete(listener);
    if (clock.listeners.size > 0) return;
    clearInterval(clock.intervalId);
    // An idle reading would be stale by the next mount, so the next mount starts a fresh clock.
    clocks.delete(intervalMs);
  };
}
const getServerSnapshot = () => undefined;

/**
 * Current time in milliseconds, refreshed every `intervalMs` by one timer shared across all
 * callers with the same interval. Returns `undefined` on the server and during the hydration
 * render, so time-relative text never mismatches the server HTML: render a time-independent
 * fallback (e.g. an absolute UTC date) while it is `undefined`. A mount after a client-side
 * navigation gets a number on its first render.
 */
export function useNow(intervalMs) {
  const $ = _c(4);
  let t0;
  if ($[0] !== intervalMs) {
    t0 = listener => subscribe(intervalMs, listener);
    $[0] = intervalMs;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const subscribeToClock = t0;
  let t1;
  if ($[2] !== intervalMs) {
    t1 = () => getClock(intervalMs).now;
    $[2] = intervalMs;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getSnapshot = t1;
  return useSyncExternalStore(subscribeToClock, getSnapshot, getServerSnapshot);
}